import React from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ResponsiveChartContainer } from '@mui/x-charts/ResponsiveChartContainer';
import { ScatterPlot } from '@mui/x-charts/ScatterChart';
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis';
import { ChartsReferenceLine } from '@mui/x-charts/ChartsReferenceLine';
import { ChartsTooltip } from '@mui/x-charts/ChartsTooltip';
import { useDrawingArea, useXScale } from '@mui/x-charts/hooks';
import { useInteractionItemProps } from '@mui/x-charts/hooks/useInteractionItemProps';
import { devicesApi } from '../api/services';
import type { DeviceSignalPoint, DeviceSignalRow, DeviceSignalView } from '../api/config';

// Domain runs from -25 (left, strongest) to -105 (right, weakest) via reverse
// axis. Readings below -105 are clipped — rare enough to not warrant the
// real estate. Left edge sits past the labeled ticks for breathing room.
const RSSI_STRONG = -25;
const RSSI_WEAK = -105;
const RSSI_MAJOR_TICKS = [-100, -90, -80, -70, -60, -50, -40, -30];
const RSSI_MINOR_TICKS = [-95, -85, -75, -65, -55, -45, -35];
// Fewer ticks for narrow viewports — labels stop colliding below ~480 px card width.
const RSSI_MAJOR_TICKS_COMPACT = [-100, -80, -60, -40];
const RSSI_MINOR_TICKS_COMPACT = [-90, -70, -50];
const FLEET_MEDIAN = -68; // median RSSI across healthy installs, last 7 d (sc-5665 query)
const FRESH_AGE_MINUTES = 15;
const SILENT_THRESHOLD_MINUTES = 32;
const MARKER_SIZE = 5;

type Shape = 'circle' | 'diamond' | 'triangle' | 'plus';

// Color per assignment. Picked to be distinct on light + dark backgrounds.
const ASSIGNMENT_COLOR: Record<DeviceSignalPoint['assignment'], string> = {
  assigned_here: '#1976d2',       // blue
  misassigned: '#ed6c02',         // orange
  unassigned_location: '#9c27b0', // purple
  no_assignment: '#757575',       // gray
};

const ASSIGNMENT_LABEL: Record<DeviceSignalPoint['assignment'], string> = {
  assigned_here: 'assigned here',
  misassigned: 'misassigned',
  unassigned_location: 'unassigned',
  no_assignment: 'unknown',
};

// Shape per assignment — color-blind-safe redundant channel.
const ASSIGNMENT_SHAPE: Record<DeviceSignalPoint['assignment'], Shape> = {
  assigned_here: 'circle',
  misassigned: 'diamond',
  unassigned_location: 'triangle',
  no_assignment: 'plus',
};

// Background zone fills (subtle — points need to stay legible).
const ZONE_FILLS = {
  happy: '#2e7d32',
  weak: '#f9a825',
  bad: '#c62828',
};
const ZONE_OPACITY = 0.08;

// Compound duration: `45 min`, `2h`, `2h 15m`, `3d`. Keeps minutes visible up
// to a day boundary so small drift is readable on the header even when the
// gateway has been silent for hours.
function formatDuration(totalMinutes: number): string {
  const m = Math.max(0, Math.round(totalMinutes));
  if (m < 60) return `${m} min`;
  const days = Math.floor(m / (60 * 24));
  if (days >= 1) return `${days}d`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem === 0 ? `${h}h` : `${h}h ${rem}m`;
}

function formatRelative(iso: string | null | undefined, now: number): string {
  if (!iso) return '—';
  const diffSec = Math.floor((now - new Date(iso).getTime()) / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  return `${formatDuration(diffSec / 60)} ago`;
}

function assignmentText(kind: DeviceSignalRow['kind'], points: DeviceSignalPoint[]): string {
  if (kind === 'local') return 'here';
  if (kind === 'unassigned_location') return 'unassigned';
  if (kind === 'other') return '—';
  const origin = points[0]?.assigned_location?.name ?? 'elsewhere';
  return `assigned to: ${origin}`;
}

// Build the series array for a row's mini ScatterChart. Each series encodes one
// (assignment × age_band) combination — up to 8 per row. Color comes from the
// assignment; fill state (filled vs hollow) comes from the age band. Shape is
// resolved in CustomScatter by reading the series id.
type ScatterPoint = { x: number; y: number; id: string; ts: string; origin?: string };
type SeriesKey = `${DeviceSignalPoint['assignment']}|fresh` | `${DeviceSignalPoint['assignment']}|old`;

function pointsToSeries(points: DeviceSignalPoint[], now: number) {
  const buckets: Record<string, ScatterPoint[]> = {};
  points.forEach((p) => {
    const ageMin = (now - new Date(p.ts).getTime()) / 60000;
    const band = ageMin < FRESH_AGE_MINUTES ? 'fresh' : 'old';
    const key: SeriesKey = `${p.assignment}|${band}`;
    (buckets[key] ||= []).push({
      x: p.rssi,
      y: 0,
      id: `${p.beacon_id}|${p.ts}`,
      ts: p.ts,
      origin: p.assigned_location?.name,
    });
  });
  return Object.entries(buckets).map(([key, data]) => {
    const [assignment] = key.split('|') as [DeviceSignalPoint['assignment'], 'fresh' | 'old'];
    return {
      type: 'scatter' as const,
      id: key,
      data,
      color: ASSIGNMENT_COLOR[assignment],
      label: ASSIGNMENT_LABEL[assignment],
      markerSize: MARKER_SIZE,
    };
  });
}

// Renders the marker shape for a single point. Fresh points are filled;
// older points are outline-only. `extraProps` receives the hover/click
// interaction handlers from the chart's InteractionContext.
function renderShape(
  shape: Shape,
  cx: number,
  cy: number,
  r: number,
  color: string,
  filled: boolean,
  key: string | number,
  extraProps: Record<string, unknown> = {},
) {
  // pointerEvents="all" on hollow shapes makes the full bounding box catch
  // mouse events; otherwise SVG only hits the stroke line.
  const paint = filled
    ? { fill: color, stroke: 'none' as const }
    : { fill: 'none' as const, stroke: color, strokeWidth: 1.5, pointerEvents: 'all' as const };
  switch (shape) {
    case 'circle':
      return <circle key={key} cx={cx} cy={cy} r={r} {...paint} {...extraProps} />;
    case 'diamond':
      return <rect key={key} x={cx - r} y={cy - r} width={r * 2} height={r * 2} transform={`rotate(45 ${cx} ${cy})`} {...paint} {...extraProps} />;
    case 'triangle': {
      const h = r * 1.8;
      const pts = `${cx},${cy - h * 0.55} ${cx - r},${cy + h * 0.45} ${cx + r},${cy + h * 0.45}`;
      return <polygon key={key} points={pts} {...paint} {...extraProps} />;
    }
    case 'plus': {
      const t = r * 0.32;
      const d =
        `M ${cx - t},${cy - r} L ${cx + t},${cy - r} L ${cx + t},${cy - t}` +
        ` L ${cx + r},${cy - t} L ${cx + r},${cy + t} L ${cx + t},${cy + t}` +
        ` L ${cx + t},${cy + r} L ${cx - t},${cy + r} L ${cx - t},${cy + t}` +
        ` L ${cx - r},${cy + t} L ${cx - r},${cy - t} L ${cx - t},${cy - t} Z`;
      return <path key={key} d={d} {...paint} {...extraProps} />;
    }
  }
}

// Custom scatter slot — renders shapes instead of plain circles and wires the
// per-point interaction handlers so the tooltip fires on hover. Without the
// handlers from useInteractionItemProps, mouse events don't reach the chart
// context and the tooltip never renders.
function CustomScatter(props: any) {
  const { series, xScale, yScale, color, markerSize } = props;
  const [assignment, band] = String(series.id).split('|') as [DeviceSignalPoint['assignment'], 'fresh' | 'old'];
  const shape = ASSIGNMENT_SHAPE[assignment] ?? 'circle';
  const filled = band !== 'old';
  const r = markerSize ?? MARKER_SIZE;
  const getInteractionItemProps = useInteractionItemProps(false);
  return (
    <g>
      {series.data.map((d: ScatterPoint, i: number) => {
        const interaction = getInteractionItemProps({ type: 'scatter', seriesId: series.id, dataIndex: i });
        return renderShape(
          shape,
          xScale(d.x),
          yScale(d.y),
          r,
          color,
          filled,
          d.id,
          interaction as Record<string, unknown>,
        );
      })}
    </g>
  );
}

// Custom tooltip content — MAC (monospace), RSSI (bold), timestamp as
// relative age, optional origin for misassigned, assignment label.
function CustomItemContent(props: any) {
  const { itemData, series } = props;
  const point = series?.data?.[itemData?.dataIndex] as ScatterPoint | undefined;
  if (!point) return null;
  const [mac] = String(point.id).split('|');
  const age = formatRelative(point.ts, Date.now());
  return (
    <Paper elevation={3} sx={{ px: 1.5, py: 1, minWidth: 180 }}>
      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{mac}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{point.x} dBm</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {age}
      </Typography>
      {point.origin && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          assigned at {point.origin}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {series.label}
      </Typography>
    </Paper>
  );
}

// Minor ticks — short unlabeled marks at the drawing-area baseline.
function MinorTicks({ values }: { values: number[] }) {
  const area = useDrawingArea();
  const xScale = useXScale();
  const xOf = (v: number) => (xScale as (n: number) => number)(v);
  const baseY = area.top + area.height;
  return (
    <g>
      {values.map((v) => {
        const x = xOf(v);
        return <line key={v} x1={x} x2={x} y1={baseY} y2={baseY + 2} stroke="#9e9e9e" strokeWidth={1} />;
      })}
    </g>
  );
}

// Fleet median label — fixed y so it sits predictably above the axis tick text.
function MedianLabel({ value }: { value: number }) {
  const xScale = useXScale();
  const x = (xScale as (n: number) => number)(value);
  return (
    <text x={x} y={38} textAnchor="middle" fontSize={9} fill="#616161" fontStyle="italic">
      median
    </text>
  );
}

// Background zone bands — rendered as SVG rects clipped to the drawing area.
function ZoneBands({ happyMin, weakMin }: { happyMin: number; weakMin: number }) {
  const area = useDrawingArea();
  const xScale = useXScale();
  const xOf = (v: number) => (xScale as (n: number) => number)(v);
  const bands = [
    { min: happyMin, max: RSSI_STRONG, fill: ZONE_FILLS.happy },
    { min: weakMin,  max: happyMin,    fill: ZONE_FILLS.weak },
    { min: RSSI_WEAK, max: weakMin,    fill: ZONE_FILLS.bad },
  ];
  return (
    <g>
      {bands.map((b) => {
        const x1 = xOf(b.min);
        const x2 = xOf(b.max);
        const x = Math.min(x1, x2);
        const w = Math.abs(x2 - x1);
        return (
          <rect
            key={b.fill}
            x={x}
            y={area.top}
            width={w}
            height={area.height}
            fill={b.fill}
            opacity={ZONE_OPACITY}
          />
        );
      })}
    </g>
  );
}

const RssiCell = React.memo(function RssiCell({ row, zoneThresholds, compact = false }: {
  row: DeviceSignalRow;
  zoneThresholds: DeviceSignalView['rssi_zone_thresholds'];
  compact?: boolean;
}) {
  // Freeze `now` per data poll (keyed on row.points) rather than the 10 s
  // header tick. The fresh → old band transition advances on the next poll —
  // acceptable for a 30 s cadence, and avoids redrawing the scatter SVG every
  // 10 s when points haven't changed.
  const series = React.useMemo(
    () => pointsToSeries(row.points, Date.now()),
    [row.points]
  );
  const majorTicks = compact ? RSSI_MAJOR_TICKS_COMPACT : RSSI_MAJOR_TICKS;
  const minorTicks = compact ? RSSI_MINOR_TICKS_COMPACT : RSSI_MINOR_TICKS;
  return (
    <Box sx={{ height: 96, width: '100%', minWidth: compact ? 0 : 320 }}>
      <ResponsiveChartContainer
        series={series}
        xAxis={[{
          id: 'x-rssi',
          min: RSSI_WEAK,
          max: RSSI_STRONG,
          reverse: true,
          tickInterval: majorTicks,
        }]}
        yAxis={[{
          id: 'y',
          min: -1,
          max: 1,
          disableLine: true,
          disableTicks: true,
          tickLabelStyle: { display: 'none' },
        }]}
        height={96}
        margin={{ top: 2, bottom: 54, left: 2, right: 2 }}
      >
        <ZoneBands happyMin={zoneThresholds.happy_min} weakMin={zoneThresholds.weak_min} />
        <ChartsReferenceLine
          x={FLEET_MEDIAN}
          lineStyle={{ stroke: '#616161', strokeDasharray: '2 2', strokeWidth: 1 }}
        />
        <MedianLabel value={FLEET_MEDIAN} />
        <ScatterPlot slots={{ scatter: CustomScatter }} />
        <MinorTicks values={minorTicks} />
        <ChartsXAxis
          axisId="x-rssi"
          position="bottom"
          disableLine
          tickSize={4}
          tickLabelStyle={{ fontSize: 10 }}
          label="RSSI (dBm)"
          labelStyle={{ fontSize: 11, fill: '#616161' }}
        />
        {row.points.length > 0 && (
          <ChartsTooltip
            trigger="item"
            slots={{ itemContent: CustomItemContent }}
          />
        )}
      </ResponsiveChartContainer>
    </Box>
  );
});

const RSSI_EXPLAINER = (
  <Box sx={{ maxWidth: 340, lineHeight: 1.45 }}>
    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
      RSSI = how loud the beacon sounds to this gateway
    </Typography>
    <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
      Measured in dBm. Less negative = stronger. −60 is louder than −80.
    </Typography>
    <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
      Every 10 points = 10× louder or quieter
    </Typography>
    <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
      −60 is 10× stronger than −70.<br />
      −60 is 100× stronger than −80.
    </Typography>
    <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
      Distance rule of thumb (open air)
    </Typography>
    <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
      Double the distance → lose about 6 points.<br />
      10× the distance → lose about 20 points.
    </Typography>
    <Typography variant="caption" sx={{ display: 'block' }}>
      Walls, metal shelves, pallets of product, and people eat signal fast. A
      beacon at −65 in free air can easily drop to −85 through a concrete wall
      or behind stacked inventory.
    </Typography>
  </Box>
);

// Summarizes the points on a row into shape+label+count buckets, one per
// distinct assignment (misassigned splits further by origin location).
// Counts are unique beacons, not readings (each beacon can carry up to 2
// readings via the ROW_NUMBER cap in the backend).
type LegendBucket = { key: string; shape: Shape; color: string; label: string; count: number };

function rowLegend(row: DeviceSignalRow): LegendBucket[] {
  const uniq: Record<string, DeviceSignalPoint> = {};
  row.points.forEach((p) => {
    if (!(p.beacon_id in uniq)) uniq[p.beacon_id] = p;
  });
  const buckets: Record<string, LegendBucket> = {};
  Object.values(uniq).forEach((p) => {
    const assignment = p.assignment;
    const key = assignment === 'misassigned'
      ? `mis:${p.assigned_location?.id ?? 'unknown'}`
      : assignment;
    if (buckets[key]) { buckets[key].count += 1; return; }
    const label = assignment === 'assigned_here'
      ? 'here'
      : assignment === 'misassigned'
        ? `from ${p.assigned_location?.name ?? 'elsewhere'}`
        : assignment === 'unassigned_location'
          ? 'unassigned'
          : 'unknown';
    buckets[key] = {
      key,
      shape: ASSIGNMENT_SHAPE[assignment],
      color: ASSIGNMENT_COLOR[assignment],
      label,
      count: 1,
    };
  });
  return Object.values(buckets);
}

function CountItem({ label, value }: { label: string; value: number | null | undefined }) {
  if (value == null) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontWeight={600}>{value}</Typography>
    </Box>
  );
}

// Single card-based layout for all viewport widths. Cards cap at ~960 px so
// they stay readable on wide monitors; the chart shrinks via ResponsiveChartContainer
// and switches to a compact tick set below ~480 px viewport width.
function SignalCards({ data, now }: { data: DeviceSignalView; now: number }) {
  const showElsewhere = data.location_device_count > 1;
  const narrow = useMediaQuery('(max-width:479.95px)');

  return (
    <Box sx={{ maxWidth: 960 }}>
      <Box sx={{ mb: 1 }}>
        <Tooltip
          title={RSSI_EXPLAINER}
          arrow
          placement="bottom-start"
          enterDelay={150}
          leaveDelay={200}
          enterTouchDelay={0}
          leaveTouchDelay={8000}
          componentsProps={{
            tooltip: { sx: { bgcolor: 'background.paper', color: 'text.primary', boxShadow: 3, border: '1px solid', borderColor: 'divider' } },
            arrow: { sx: { color: 'background.paper' } },
          }}
        >
          <Button
            size="small"
            variant="outlined"
            startIcon={<InfoOutlinedIcon />}
            aria-label="What is RSSI?"
            sx={{ fontWeight: 600, textTransform: 'none' }}
          >
            What&apos;s RSSI?
          </Button>
        </Tooltip>
      </Box>

      {data.rows.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary">No beacon data yet for this device.</Typography>
        </Paper>
      ) : (
        <Stack spacing={1}>
          {data.rows.map((row, idx) => {
            const legend = rowLegend(row);
            return (
              <Paper
                key={`${row.kind}-${row.sku ?? 'other'}-${idx}`}
                variant="outlined"
                sx={{ px: 1.5, py: 1.5 }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.75 }}>
                  <Typography variant="subtitle2">{row.sku ?? 'Other beacons'}</Typography>
                  {/* Label is implied for `local` rows (SKU has active BAs at
                      this location). Non-local rows spell out where they belong. */}
                  {row.kind !== 'local' && (
                    <Typography variant="caption" color="text.secondary">
                      {assignmentText(row.kind, row.points)}
                    </Typography>
                  )}
                </Stack>
                <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                  <CountItem label="Assigned" value={row.roster_count} />
                  <CountItem label="Seen" value={row.seen_count} />
                  {showElsewhere && <CountItem label="Elsewhere" value={row.seen_elsewhere_count} />}
                  <CountItem label="Missing" value={row.not_seen_count} />
                </Stack>
                <RssiCell row={row} zoneThresholds={data.rssi_zone_thresholds} compact={narrow} />
                {legend.length > 0 && (
                  <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                    {legend.map((b) => (
                      <Stack key={b.key} direction="row" spacing={0.5} alignItems="center">
                        <LegendShape shape={b.shape} color={b.color} />
                        <Typography variant="caption" color="text.secondary">
                          {b.count} {b.label}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Paper>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}

function LegendShape({ shape, color, filled = true }: { shape: Shape; color: string; filled?: boolean }) {
  return (
    <svg width={14} height={14} style={{ flexShrink: 0 }}>
      <g>{renderShape(shape, 7, 7, 4.5, color, filled, 'legend')}</g>
    </svg>
  );
}

type GatewayState = 'normal' | 'fresh' | 'silent';

function gatewayState(data: DeviceSignalView, now: number): GatewayState {
  if (data.silent_long_term) return 'silent';
  if (data.last_publish_at === null) return 'fresh';
  const ageMin = (now - new Date(data.last_publish_at).getTime()) / 60000;
  if (ageMin > SILENT_THRESHOLD_MINUTES) return 'silent';
  return 'normal';
}

function StatusChip({ state }: { state: GatewayState }) {
  if (state === 'normal') return null;
  const config = state === 'fresh'
    ? { label: 'WAITING', color: '#616161', bg: '#eeeeee' }
    : { label: 'SILENT', color: '#e65100', bg: '#fff3e0' };
  return (
    <Chip
      size="small"
      label={config.label}
      sx={{ bgcolor: config.bg, color: config.color, fontWeight: 700, letterSpacing: 0.5, fontSize: 10 }}
    />
  );
}

function StagingChip() {
  return (
    <Chip
      size="small"
      label="STAGING"
      sx={{ bgcolor: '#fff8e1', color: '#8d6e00', fontWeight: 700, letterSpacing: 0.5, fontSize: 10 }}
    />
  );
}

function Header({ data, now }: { data: DeviceSignalView; now: number }) {
  const state = gatewayState(data, now);
  const title = data.device.location?.name || data.device.label || data.device.device_id;
  const titleEl = data.device.location ? (
    <Link
      component={RouterLink}
      to={`/locations/${data.device.location.id}/dashboard`}
      underline="hover"
      color="inherit"
    >
      {title}
    </Link>
  ) : title;

  return (
    <Stack spacing={1} sx={{ mb: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
        <Typography variant="h5" component="h1" sx={{ lineHeight: 1.2 }}>
          {titleEl}
        </Typography>
        {data.device.staging && <StagingChip />}
        <StatusChip state={state} />
      </Stack>
      <Typography variant="body2" color="text.secondary">
        Last publish {formatRelative(data.last_publish_at, now)}
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Chip size="small" label={`${data.rssi_tally.happy} happy`} sx={{ bgcolor: '#e8f5e9', color: '#1b5e20' }} />
        <Chip size="small" label={`${data.rssi_tally.weak} weak`} sx={{ bgcolor: '#fff8e1', color: '#8d6e00' }} />
        <Chip size="small" label={`${data.rssi_tally.bad} bad`} sx={{ bgcolor: '#ffebee', color: '#b71c1c' }} />
        {data.last_publish_at !== null && (
          <Chip
            size="small"
            label={`${data.missing_total} missing`}
            sx={data.missing_total === 0
              ? { bgcolor: '#e8f5e9', color: '#1b5e20' }
              : { bgcolor: '#ffebee', color: '#b71c1c' }}
          />
        )}
      </Stack>
    </Stack>
  );
}

export const DeviceDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const numericId = id ? Number(id) : NaN;

  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    const handle = window.setInterval(() => setNow(Date.now()), 10_000);
    return () => window.clearInterval(handle);
  }, []);

  const { data, isLoading, error } = useQuery({
    queryKey: ['deviceSignalView', numericId],
    queryFn: () => devicesApi.getSignalView(numericId),
    refetchInterval: 30_000,
    enabled: Number.isFinite(numericId),
  });

  if (!Number.isFinite(numericId)) {
    return <Alert severity="error">Invalid device id.</Alert>;
  }
  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;
  }
  if (!data) {
    return <Alert severity="error">Failed to load device signal view.</Alert>;
  }

  return (
    <Box>
      <Header data={data} now={now} />
      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Couldn't refresh from the server — showing the last successful load.
        </Alert>
      )}
      <SignalCards data={data} now={now} />
    </Box>
  );
};
